import React, { Suspense } from 'react';
import { forTemplate } from '@trello/i18n';
import { useRecentlyUsedFeaturePreloader } from '@trello/recently-used-feature-preloader';
import { memberId } from '@trello/session-cookie';
import { HeaderMenu } from 'app/src/components/HeaderMenu';
import { STARRED_BOARDS_MENU_POPOVER_CLASSNAME } from './DragAndDropStarredBoardsList';
import { ErrorBoundary } from 'app/src/components/ErrorBoundary';
import {
  BoardStarsDocument,
  BoardStarsQueryVariables,
} from './BoardStarsQuery.generated';
import { StarredBoardsMenuTestIds } from '@trello/test-ids';
import { ChunkLoadErrorBoundary } from 'app/src/components/ChunkLoadErrorBoundary';

const format = forTemplate('starred_boards_menu');

const ErrorComponent: React.FC = () => <p>{format('there-was-an-error')}</p>;

export const StarredBoardsMenuButton: React.FC = () => {
  const [
    StarredBoardsMenuPopover,
    trackFeatureUsage,
  ] = useRecentlyUsedFeaturePreloader({
    featureName: 'StarredBoardsMenuPopover',
    useLazyComponentArgs: [
      () =>
        import(
          /* webpackChunkName: "starred-boards-menu-popover" */ './StarredBoardsMenuPopover'
        ),
      { namedImport: 'StarredBoardsMenuPopover' },
    ],
    preloadQueryOptions: {
      query: BoardStarsDocument,
      variables: { memberId: memberId ?? 'me' } as BoardStarsQueryVariables,
    },
  });

  return (
    <HeaderMenu
      buttonText={format('starred')}
      analyticsButtonName="starredBoardsMenuButton"
      analyticsComponentName="starredBoardsMenuInlineDialog"
      popoverTitle={format('starred-boards')}
      dangerous_classNamePopover={STARRED_BOARDS_MENU_POPOVER_CLASSNAME}
      dataTestId={StarredBoardsMenuTestIds.StarredBoardsMenu}
      onClick={trackFeatureUsage}
    >
      <ErrorBoundary
        tags={{
          ownershipArea: 'trello-teamplates',
          feature: 'Starred Boards Menu Button',
        }}
        errorHandlerComponent={ErrorComponent}
      >
        <Suspense fallback={null}>
          <ChunkLoadErrorBoundary fallback={ErrorComponent}>
            <StarredBoardsMenuPopover />
          </ChunkLoadErrorBoundary>
        </Suspense>
      </ErrorBoundary>
    </HeaderMenu>
  );
};
